// Microphone capture for the Learn exercises (issue #3481): the mic track, the shared audio
// module's capture worklet and analysis worker (client/src/audio/), all on the Learn audio
// session's one long-lived AudioContext. The session (learn_audio_session.ts) exposes this behind
// openMic / endMic / onFrame / setTargetMidi so the Range Finder and Pitch Match never touch
// getUserMedia, worklets or workers themselves, and nothing a platform needs differently in its
// capture reaches the exercises.
//
// The shared audio module's pieces are imported, not copied: capture_worklet.ts (loaded into the
// context once), analysis_worker.ts (one module worker per open mic) and, inside the worker, the
// YIN and known-target detectors. The audio lab imports the same pieces; its AudioLabEngine class
// itself is not used because it creates and owns its own AudioContext, which the
// one-context-per-page rule forbids.
//
// Graph while the mic is open:
//
//   MediaStreamAudioSourceNode ────────▶ input 0 ┐         sung onsets, raw chunks ─▶ main thread
//                                                ├ AudioWorkletNode (syp-capture) ─▶ GainNode (gain 0) ─▶ destination
//   guide bus ─▶ GainNode (mono tap) ──▶ input 1 ┘        │ frames over a MessageChannel port
//       │                                                 ▼
//       └─▶ destination (the guide)                analysis worker ─▶ 'result' messages ─▶ CaptureFrame listeners
//
// The zero-gain leg exists because Safari only pulls audio through a worklet that reaches the
// destination (audio_lab_engine.ts). The second input is the guide reference (issue #3708): the
// session's guide bus, through which every sound the session plays reaches the speakers, so the
// worklet sees the microphone and the guide that echoes into it in the same render quantum. The
// worklet reads only the first channel of that input and the soundfont's notes may be stereo, so a
// mono tap (a unity GainNode with one explicit channel and speaker down-mixing, made by the shared
// audio module's guide_reference.ts, as the audio lab's is) sits between the bus and the worklet; the
// microphone path and the worklet's own channel settings are untouched.
// The bus is the session's and stays connected to the destination: this capture adds the tap to it
// and takes only the tap away again. The capture policy (client/src/audio/capture_policy.ts)
// decides the browser's echo cancellation per platform, and is asked once per mic open, before
// getUserMedia; noise suppression and automatic gain stay off on every platform. The detectors run
// with YIN on and McLeod off (slow phones), plus the known-target estimator whenever a target note
// is set.
//
// The app's own echo canceller (issue #3708). Where the policy turns the browser's cancellation off
// and asks for the app's own (Android), the worker is configured with the canceller on, and once the
// graph is connected the worklet is told to stream the guide reference to the worker, a hop at a
// time over the frame port; the worker removes the guide's echo from each window before the
// detectors read it (client/src/audio/analyse_frame.ts) and says how in each frame's `echo`. The
// canceller finds the guide's delay into the microphone itself, over its whole range, and Learn gives
// it no hint: the canceller searches only 150 ms either side of one, and a hint can be wrong by a
// lot - the stored Rhythm calibration offset is a tap round trip with touch latency in it, and one
// A16 page session put the guide into the microphone 1.07 s after it left the page against a 412 ms
// acoustic round trip (docs/learn/audio-research.md, 2026-10-03). The whole-range search costs a
// fraction of a second at the first note and nothing after.
// The one policy object asked at open decides both the constraints and the canceller, and stays on
// the open graph. Elsewhere (iOS, desktop) nothing is streamed and nothing is cancelled.
//
// Rolling capture (issue #3587). While recording is on, the worklet also streams the raw mic
// samples back to the main thread on its own port, in chunks stamped with the audio-clock time of
// their first sample, and the last captureKeepSeconds of them are kept in a ring. A flicker seen on
// the screen can then be saved after the fact, with the audio that produced it, and replayed on a
// developer's machine. Each chunk carries the guide reference for exactly its span, kept and trimmed
// with it, so a saved moment has the guide beside the microphone sample for sample (issue #3708).
// The ring is memory only, and ending the mic drops it.
//
// Sung onsets (issue #3723). While onsets are on, the worklet also reports the start of every sung
// note, timed on the audio thread (client/src/audio/onset_detector.ts), and they reach the
// exercise through onOnset as SungOnsets on the audio clock. Sing Rhythm scores those, not frames:
// a frame says what the last 43 ms sounded like and a slow phone skips some, an onset says when a
// sound began. The frames keep coming beside them, for the "Hearing you" meter.
//
// Ending the mic stops the track, tears the nodes and the worker down and leaves the context
// exactly as it was: the context is the session's, which keeps it running, and nothing here ever
// suspends or closes it. It also drops an open that is still in flight, so the next exercise opens
// a graph of its own rather than joining an attempt that has been abandoned.
//
// The pieces that touch the browser (getUserMedia, addModule, AudioWorkletNode, Worker), the
// capture policy are behind MicCaptureOptions so the unit tests run
// the real wiring against fakes under jsdom.

import { CapturePolicy, capturePolicyFor } from '../../../audio/capture_policy.ts';
import {
  captureNodeOptions,
  connectReference,
  joinRecordedChunks,
  RecordedChunk,
} from '../../../audio/guide_reference.ts';
import { midiToFrequency } from '../../../audio/pitch/notes.ts';
import {
  captureProcessorName,
  defaultDetectorConfig,
  DetectorConfig,
  EchoReading,
  FrameResult,
  MainToWorkerMessage,
  MainToWorkletMessage,
  WorkerToMainMessage,
  WorkletToMainMessage,
} from '../../../audio/pitch/types.ts';
import { getDevice } from '../../../util/platform.ts';

// ---- Types ------------------------------------------------------------------------------------

// One analysed frame of microphone audio, as the exercises see it. `timeMs` is on the audio clock
// (the same clock LearnAudioSession.nowMs reads), at the end of the analysed window.
export interface CaptureFrame {
  timeMs: number;
  dbfs: number; // -Infinity for digital silence
  pitch?: { frequency: number; clarity: number }; // YIN, the general detector
  target?: { cents: number; clarity: number; octaveBelow: number; octaveAbove: number }; // the known-target estimator, when a target is set
  // The app's own echo canceller's account of the window, where the capture policy runs it; dbfs
  // above is then the cleaned window's whenever echo.cancelled, and echo.micDbfs the raw level
  echo?: EchoReading;
}

// The start of one sung note (issue #3723). `timeMs` is on the audio clock, when the sound reached
// the microphone: later than the singer made it by the device's input latency, which the exercise's
// warm-up measures. `peakRms` is how loud it got.
export interface SungOnset {
  timeMs: number;
  peakRms: number;
}

// How much raw microphone audio the rolling capture keeps (issue #3587): enough to reach back past
// a flicker the singer noticed and then went to save
export const captureKeepSeconds = 30;

// The rolling capture's audio, oldest sample first. `startTimeMs` is on the audio clock (the clock
// CaptureFrame.timeMs is on) at `samples[0]`, which is what lines the audio up with the frames
// analysed from it. `reference` is the guide the session played over the same span (issue #3708):
// the same length as `samples` and aligned with it sample for sample, silent where nothing played.
export interface CaptureRecording {
  samples: Float32Array; // the microphone
  reference: Float32Array; // the guide reference
  sampleRate: number;
  startTimeMs: number;
}

// Why the microphone could not be opened; each has its own plain-language screen
export type MicFailure = 'denied' | 'unavailable' | 'busy' | 'unknown';

export class MicOpenError extends Error {
  constructor(
    readonly failure: MicFailure,
    readonly cause?: unknown,
  ) {
    super(`microphone ${failure}`);
    this.name = 'MicOpenError';
  }
}

// What a page shows for whatever openMic rejected with: a MicOpenError names its own failure, and
// anything else is one we cannot name
export function micFailureOf(error: unknown): MicFailure {
  return error instanceof MicOpenError ? error.failure : 'unknown';
}

// Maps a getUserMedia rejection onto a MicFailure by its DOMException name
export function micFailureFromError(error: unknown): MicFailure {
  const name = (error as { name?: unknown } | undefined)?.name;
  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'denied';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return 'unavailable';
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return 'busy';
    default:
      return 'unknown';
  }
}

// The part of a Worker the capture uses; a test passes a plain object
export interface WorkerLike {
  postMessage(message: MainToWorkerMessage, transfer?: Transferable[]): void;
  onmessage: ((event: MessageEvent<WorkerToMainMessage>) => void) | null;
  terminate(): void;
}

export interface MicCaptureOptions {
  getUserMedia?: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  // The analysis worker; the default is the shared audio module's, as a module worker
  createWorker?: () => WorkerLike;
  // Loads the capture worklet module into `context`; called once per context
  loadWorklet?: (context: AudioContext) => Promise<void>;
  // The capture worklet node, after loadWorklet resolved
  createWorkletNode?: (context: AudioContext, options: AudioWorkletNodeOptions) => AudioWorkletNode;
  // How this platform captures; asked once per mic open. The default reads this device's policy.
  capturePolicy?: () => CapturePolicy;
}

export interface MicCapture {
  open(): Promise<void>; // rejects with MicOpenError; resolves at once when already open
  end(): void; // stops the track and tears the graph down; the context is untouched
  isOpen(): boolean;
  onFrame(listener: (frame: CaptureFrame) => void): () => void; // returns the unsubscribe
  setTargetMidi(midi: number | undefined): void; // remembered, so a mic opened later gets it too
  // Keeps the last captureKeepSeconds of raw mic audio, and the guide reference beside it, while on;
  // remembered like the target note, so a mic opened later records too. Turning it off drops what
  // was kept.
  setRecording(enabled: boolean): void;
  // Reports the start of every sung note while on; remembered like the target note, so a mic opened
  // later reports too. Turning it on again starts the detector afresh.
  setOnsets(enabled: boolean): void;
  onOnset(listener: (onset: SungOnset) => void): () => void; // returns the unsubscribe
  // A copy of the kept audio, oldest first; undefined when nothing is kept. The ring is left as it was.
  takeRecording(): CaptureRecording | undefined;
  // Whether the ring holds any audio at all: what takeRecording would answer, without the copy, so a
  // page that re-renders on every frame can ask it on every render
  hasRecording(): boolean;
}

// What getUserMedia is asked for under a capture policy: the browser's echo cancellation as the
// policy says, noise suppression and automatic gain always off, as in the audio lab's defaults
export function micConstraintsFor(policy: CapturePolicy): MediaStreamConstraints {
  return {
    audio: { echoCancellation: policy.echoCancellation, noiseSuppression: false, autoGainControl: false },
    video: false,
  };
}

// What the browser says about the microphone for this origin before anything is asked for, plus
// 'unknown' for every browser that will not say (issue #3510)
export type MicPermissionState = 'granted' | 'denied' | 'prompt' | 'unknown';

// Asks the Permissions API whether the microphone is already granted, so an exercise can skip
// asking for something the singer has already allowed. Everything that can go wrong here means
// 'unknown': the API is missing (older WebKit), or it refuses this name (Firefox throws for
// 'microphone'), or it rejects for a reason of its own. Only 'granted' is ever acted on, and
// 'granted' is still no promise that getUserMedia succeeds - the device may be gone or busy.
export async function queryMicPermission(): Promise<MicPermissionState> {
  try {
    const status = await navigator.permissions.query({ name: 'microphone' as PermissionName });
    return status.state;
  } catch {
    return 'unknown';
  }
}

// ---- Implementation ---------------------------------------------------------------------------

const defaultOptions: Required<MicCaptureOptions> = {
  getUserMedia: (constraints) => navigator.mediaDevices.getUserMedia(constraints),
  createWorker: () => new Worker(new URL('../../../audio/analysis_worker.ts', import.meta.url), { type: 'module' }),
  loadWorklet: async (context) => {
    // Vite's ?worker&url import gives the worklet module its own URL; loaded lazily so a unit
    // test under jsdom, which injects loadWorklet, never resolves the asset
    const { default: captureWorkletUrl } = await import('../../../audio/capture_worklet.ts?worker&url');
    await context.audioWorklet.addModule(captureWorkletUrl);
  },
  createWorkletNode: (context, options) => new AudioWorkletNode(context, captureProcessorName, options),
  capturePolicy: () => capturePolicyFor(getDevice()),
};

interface OpenGraph {
  policy: CapturePolicy; // asked once at open: the constraints and the canceller both come from it
  stream: MediaStream;
  source: MediaStreamAudioSourceNode;
  worklet: AudioWorkletNode;
  silentGain: GainNode;
  disconnectReference: () => void; // takes the guide bus's tap into the worklet's second input away
  worker: WorkerLike;
}

class WebAudioMicCapture implements MicCapture {
  private readonly options: Required<MicCaptureOptions>;
  private graph: OpenGraph | undefined;
  private opening: Promise<void> | undefined;
  private generation = 0; // bumped by end(); an open() still awaiting getUserMedia sees it and stops the track
  private workletLoaded = false;
  private targetMidi: number | undefined;
  private recording = false;
  private ring: RecordedChunk[] = []; // the rolling capture as the worklet sent it, oldest chunk first
  private ringSamples = 0; // the samples the ring holds between its chunks
  private onsets = false;
  private readonly listeners = new Set<(frame: CaptureFrame) => void>();
  private readonly onsetListeners = new Set<(onset: SungOnset) => void>();

  constructor(
    private readonly context: AudioContext,
    private readonly reference: AudioNode, // the guide bus; see the header
    options: MicCaptureOptions = {},
  ) {
    this.options = { ...defaultOptions, ...options };
  }

  isOpen(): boolean {
    return this.graph !== undefined;
  }

  onFrame(listener: (frame: CaptureFrame) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  onOnset(listener: (onset: SungOnset) => void): () => void {
    this.onsetListeners.add(listener);
    return () => {
      this.onsetListeners.delete(listener);
    };
  }

  setOnsets(enabled: boolean) {
    this.onsets = enabled;
    if (this.graph) {
      this.tellWorkletOnsets(this.graph.worklet);
    }
  }

  private tellWorkletOnsets(worklet: AudioWorkletNode) {
    const message: MainToWorkletMessage = { type: 'onsets', enabled: this.onsets };
    worklet.port.postMessage(message);
  }

  setTargetMidi(midi: number | undefined) {
    this.targetMidi = midi;
    this.graph?.worker.postMessage({ type: 'config', config: { targetFrequency: this.targetFrequency() } });
  }

  private targetFrequency(): number | undefined {
    return this.targetMidi === undefined ? undefined : midiToFrequency(this.targetMidi);
  }

  setRecording(enabled: boolean) {
    this.recording = enabled;
    if (!enabled) {
      this.dropRecording();
    }
    if (this.graph) {
      this.tellWorkletToRecord(this.graph.worklet);
    }
  }

  takeRecording(): CaptureRecording | undefined {
    if (this.ringSamples === 0) {
      return undefined;
    }
    // The ring's first sample is its oldest chunk's first, and the worklet stamps every chunk
    const { samples, reference, startTimeSec } = joinRecordedChunks(this.ring);
    if (startTimeSec === undefined) {
      return undefined;
    }
    return { samples, reference, sampleRate: this.context.sampleRate, startTimeMs: startTimeSec * 1000 };
  }

  hasRecording(): boolean {
    return this.ringSamples > 0;
  }

  private dropRecording() {
    this.ring = [];
    this.ringSamples = 0;
  }

  // The worklet streams raw chunks back only while it is told to
  private tellWorkletToRecord(worklet: AudioWorkletNode) {
    const message: MainToWorkletMessage = { type: 'record', enabled: this.recording };
    worklet.port.postMessage(message);
  }

  // A second open() while one is in flight joins it - that is how the Range Finder's permission
  // screen finishes the open its mount started. An open() after an end() joins nothing: end()
  // dropped that attempt, and only the attempt that is still the current one may clear the field
  // when it finishes, or a stale one would clear a fresher open out from under its callers.
  open(): Promise<void> {
    if (this.graph) {
      return Promise.resolve();
    }
    if (!this.opening) {
      const opening: Promise<void> = this.openGraph().finally(() => {
        if (this.opening === opening) {
          this.opening = undefined;
        }
      });
      this.opening = opening;
    }
    return this.opening;
  }

  private async openGraph(): Promise<void> {
    const generation = this.generation;
    // Asked here, once per attempt: an open() that joins this one, or finds the mic already open,
    // never gets this far
    const policy = this.options.capturePolicy();
    let stream: MediaStream;
    try {
      stream = await this.options.getUserMedia(micConstraintsFor(policy));
    } catch (error) {
      throw new MicOpenError(micFailureFromError(error), error);
    }
    if (generation !== this.generation) {
      stopTracks(stream); // ended while the permission prompt was up; the exercise has left
      return;
    }
    // The stream is granted and nothing holds it yet: a worklet module that will not load, or a
    // browser that throws while the graph is built, would otherwise leave the track live with no
    // one reading it - the recording indicator on and the microphone busy for everything else
    // until the tab is closed (issue #3518). So the track is stopped before the failure leaves.
    //
    // The failure is 'unknown', never one of the getUserMedia names: nothing here is the device
    // answering for itself. addModule rejects with an AbortError when the module will not parse,
    // and telling the singer their microphone is busy because a script failed to load would send
    // them off closing other apps (issue #3518).
    try {
      if (!this.workletLoaded) {
        await this.options.loadWorklet(this.context);
        this.workletLoaded = true;
      }
      if (generation !== this.generation) {
        stopTracks(stream);
        return;
      }
      this.graph = this.buildGraph(stream, policy);
    } catch (error) {
      stopTracks(stream);
      throw new MicOpenError('unknown', error);
    }
  }

  // The worker is started before the nodes it reads, so a node constructor that throws leaves a
  // worker running with nothing to feed it: one per failed open, alive until the tab is closed.
  // It is torn down here rather than by the caller, because this is where it was started.
  private buildGraph(stream: MediaStream, policy: CapturePolicy): OpenGraph {
    const worker = this.options.createWorker();
    try {
      return this.connectGraph(stream, policy, worker);
    } catch (error) {
      guarded(() => {
        worker.onmessage = null;
        worker.terminate();
      });
      throw error;
    }
  }

  private connectGraph(stream: MediaStream, policy: CapturePolicy, worker: WorkerLike): OpenGraph {
    const { context } = this;
    const config: DetectorConfig = {
      ...defaultDetectorConfig,
      sampleRate: context.sampleRate,
      enableYin: true,
      enableMpm: false,
      targetFrequency: this.targetFrequency(),
    };
    if (policy.ownCanceller) {
      config.canceller = { enabled: true }; // no delay hint: see the header
    }
    worker.onmessage = (event) => this.onWorkerMessage(event.data);
    worker.postMessage({ type: 'config', config });

    const source = context.createMediaStreamSource(stream);
    // Input 1 is the guide reference; the worklet reads it only when the processor option says so
    const worklet = this.options.createWorkletNode(context, captureNodeOptions(config.windowSize, config.hopSize));
    worklet.port.onmessage = (event: MessageEvent<WorkletToMainMessage>) => this.onWorkletMessage(event.data);
    // frames go from the worklet straight to the worker over a channel of their own
    const channel = new MessageChannel();
    worker.postMessage({ type: 'port', port: channel.port1 }, [channel.port1]);
    const portMessage: MainToWorkletMessage = { type: 'port', port: channel.port2 };
    worklet.port.postMessage(portMessage, [channel.port2]);

    const silentGain = context.createGain();
    silentGain.gain.value = 0;
    source.connect(worklet);
    worklet.connect(silentGain);
    silentGain.connect(context.destination);
    // The reference goes last, and attaches to the bus last of all, so a browser that throws while
    // the graph is built leaves nothing hanging off the guide
    const disconnectReference = connectReference(context, this.reference, worklet);
    if (this.recording) {
      this.tellWorkletToRecord(worklet); // asked for before this graph existed
    }
    if (this.onsets) {
      this.tellWorkletOnsets(worklet); // likewise
    }
    if (policy.ownCanceller) {
      this.tellWorkletToStreamReference(worklet, true);
    }
    return { policy, stream, source, worklet, silentGain, disconnectReference, worker };
  }

  // The worklet streams the guide reference to the worker, for the canceller, only while told to
  private tellWorkletToStreamReference(worklet: AudioWorkletNode, enabled: boolean) {
    const message: MainToWorkletMessage = { type: 'reference', enabled };
    worklet.port.postMessage(message);
  }

  private onWorkerMessage(message: WorkerToMainMessage) {
    if (message.type !== 'result' || !this.graph) {
      return;
    }
    const frame = toCaptureFrame(message.result);
    for (const listener of this.listeners) {
      listener(frame);
    }
  }

  // Only the sung onsets and the raw chunks matter here; the worklet's stats and latency clicks are
  // the audio lab's. An onset still in flight when onsets were turned off is dropped. The oldest
  // chunks go once the ring holds more than captureKeepSeconds, and a chunk that arrives while
  // recording is off (the worklet flushes its last partial chunk when told to stop) is not kept.
  private onWorkletMessage(message: WorkletToMainMessage) {
    if (message.type === 'onset') {
      if (!this.onsets || !this.graph) {
        return;
      }
      const onset: SungOnset = { timeMs: message.time * 1000, peakRms: message.peakRms };
      for (const listener of this.onsetListeners) {
        listener(onset);
      }
      return;
    }
    if (message.type !== 'recordChunk' || !this.recording || !this.graph) {
      return;
    }
    this.ring.push({ samples: message.samples, reference: message.reference, time: message.time });
    this.ringSamples += message.samples.length;
    const keepSamples = captureKeepSeconds * this.context.sampleRate;
    while (this.ringSamples > keepSamples) {
      const oldest = this.ring.shift()!;
      this.ringSamples -= oldest.samples.length;
    }
  }

  // Every step is guarded on its own: a media track, a worklet node and a worker are all browser
  // objects that could throw, and the exercise that is leaving still has a context to rest and a
  // guide to silence afterwards. Nothing here is worth failing a teardown over.
  end() {
    this.generation += 1;
    // An open still in flight belongs to the exercise that has just left: it will find the bumped
    // generation and stop whatever track it is given, and no later open() may join it - the mic
    // that open was asked for is not the mic the next exercise is asking for.
    this.opening = undefined;
    // The rolling capture belongs to the mic that is ending; the next mic keeps a ring of its own
    this.dropRecording();
    const graph = this.graph;
    if (!graph) {
      return;
    }
    this.graph = undefined;
    guarded(() => stopTracks(graph.stream));
    guarded(() => graph.source.disconnect());
    if (graph.policy.ownCanceller) {
      guarded(() => this.tellWorkletToStreamReference(graph.worklet, false));
    }
    guarded(() => {
      graph.worklet.port.onmessage = null;
    });
    guarded(() => graph.worklet.disconnect());
    guarded(() => graph.silentGain.disconnect());
    // Only the tap this capture added to the guide bus: the bus itself stays connected to the
    // destination, because the guide plays on after the mic has ended
    graph.disconnectReference(); // guarded step by step itself
    guarded(() => {
      graph.worker.onmessage = null;
      graph.worker.terminate();
    });
  }
}

// One teardown step; a browser that throws on it must not cost the steps after it (see end())
function guarded(step: () => void) {
  try {
    step();
  } catch {
    // the object was already gone, or this browser throws on tearing it down: nothing left to do
  }
}

function stopTracks(stream: MediaStream) {
  for (const track of stream.getTracks()) {
    track.stop();
  }
}

export function toCaptureFrame(result: FrameResult): CaptureFrame {
  const frame: CaptureFrame = { timeMs: result.time * 1000, dbfs: result.dbfs };
  if (result.yin) {
    frame.pitch = { frequency: result.yin.frequency, clarity: result.yin.clarity };
  }
  if (result.target) {
    const { cents, clarity, octaveBelow, octaveAbove } = result.target;
    frame.target = { cents, clarity, octaveBelow, octaveAbove };
  }
  if (result.echo) {
    const { micDbfs, echoDbfs, residualDbfs, delayMs, converged, cancelled } = result.echo;
    frame.echo = { micDbfs, echoDbfs, residualDbfs, delayMs, converged, cancelled };
  }
  return frame;
}

// `reference` is the guide bus: everything the speaker plays, which the capture records beside the
// microphone (see the header)
export function createMicCapture(
  context: AudioContext,
  reference: AudioNode,
  options: MicCaptureOptions = {},
): MicCapture {
  return new WebAudioMicCapture(context, reference, options);
}
